/**
 * Image Cache Service
 * 
 * Provides functionality to cache player images in the browser's sessionStorage
 * to reduce API calls and manage rate limits.
 */

// Cache key for session storage
const IMAGE_CACHE_KEY = 'dream11_player_images_cache';

// Interface for cache entry
interface CacheEntry {
  url: string;
  timestamp: number;
}

// Interface for the image cache object
interface ImageCache {
  [key: string]: CacheEntry;
}

/**
 * Initialize the image cache from sessionStorage
 */
const initializeCache = (): ImageCache => {
  try {
    const cachedData = sessionStorage.getItem(IMAGE_CACHE_KEY);
    return cachedData ? JSON.parse(cachedData) : {};
  } catch (error) {
    console.error('Error initializing image cache:', error);
    return {};
  }
};

// Cache singleton
let imageCache: ImageCache = initializeCache();

/**
 * Get an image URL from the cache
 * @param playerId Player ID to use as cache key
 * @param playerName Player name as a backup identifier
 */
export const getCachedImage = (playerId: string, playerName: string): string | null => {
  // Create a composite key in case the player ID is not reliable
  const cacheKey = `${playerId}_${playerName.toLowerCase().replace(/[^a-z0-9]/g, '')}`;
  
  // Check if we have a cached entry
  const cacheEntry = imageCache[cacheKey];
  
  if (cacheEntry) {
    // Check if the cache entry is still valid (24 hours)
    const now = Date.now();
    const cacheAge = now - cacheEntry.timestamp;
    const cacheLifetime = 24 * 60 * 60 * 1000; // 24 hours
    
    if (cacheAge < cacheLifetime) {
      console.log(`Using cached image for ${playerName}`);
      return cacheEntry.url;
    } else {
      // Cache is too old, remove it
      delete imageCache[cacheKey];
      persistCache();
    }
  }
  
  return null;
};

/**
 * Store an image URL in the cache
 * @param playerId Player ID to use as cache key
 * @param playerName Player name as a backup identifier
 * @param imageUrl The image URL to cache
 */
export const cacheImage = (playerId: string, playerName: string, imageUrl: string): void => {
  // Don't cache placeholder images
  if (imageUrl.includes('placeholder.com')) {
    return;
  }
  
  // Create a composite key
  const cacheKey = `${playerId}_${playerName.toLowerCase().replace(/[^a-z0-9]/g, '')}`;
  
  // Store the new cache entry
  imageCache[cacheKey] = {
    url: imageUrl,
    timestamp: Date.now()
  };
  
  // Persist the cache to sessionStorage
  persistCache();
  
  console.log(`Cached image for ${playerName}`);
};

/**
 * Persist the cache to sessionStorage
 */
const persistCache = (): void => {
  try {
    sessionStorage.setItem(IMAGE_CACHE_KEY, JSON.stringify(imageCache));
  } catch (error) {
    console.error('Error persisting image cache:', error);
    
    // If storage limit exceeded, clear oldest entries
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      pruneCache();
      persistCache();
    }
  }
};

/**
 * Remove oldest entries from the cache if it gets too large
 */
const pruneCache = (): void => {
  // Get all cache entries with timestamps
  const entries = Object.entries(imageCache)
    .map(([key, entry]) => ({ key, timestamp: entry.timestamp }))
    .sort((a, b) => a.timestamp - b.timestamp);
  
  // Delete the oldest 25% of entries
  const pruneCount = Math.ceil(entries.length * 0.25);
  
  for (let i = 0; i < pruneCount; i++) {
    delete imageCache[entries[i].key];
  }
  
  console.log(`Pruned ${pruneCount} old entries from image cache`);
};

/**
 * Clear the entire image cache
 */
export const clearImageCache = (): void => {
  imageCache = {};
  sessionStorage.removeItem(IMAGE_CACHE_KEY);
  console.log('Image cache cleared');
}; 